import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelectedRowsStore } from "@/store/selectedRowsStore.ts";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { useProjectsStore } from "@/store/projectsStore.ts";
import SVGDownloadFile from "@/assets/icons/SVGDownloadFile.tsx";
import { ProjectApiResponse } from "@/types";
import { formatDate, formatExportDate } from "@/utils/formatDate.ts";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter.ts";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import { removeTrailZero } from "@/utils/numberFormatting";
import { downloadCSV } from "@/utils/downloadCSV.ts";
import { ProjectsCSVHeaders } from "@/constants";
import {
  calculateTotalNominalPower,
  calculateTotalUsableCapacity,
} from "@/utils/calculateTotalNominalPower";
import {
  getAllProjectsToExportReq,
  getProjectsToExportReq,
} from "@/api/projectsApi";
import { replaceUnderscoresWithSpaces } from "@/utils/replaceUnderscoreWithSpaces.ts";
import dayjs from "dayjs";
import { useAuthStore } from "@/store/authStore";

type Props = {
  pto: boolean;
  search: string;
  filter: string[];
  tableId: string;
  batteryOperationalized?: string;
};

const ProjectsSelectedInfo = ({ search, filter, pto, tableId, batteryOperationalized }: Props) => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const role = useAuthStore((state) => state.role);
  const isContractor = role === "contractor";
  const selectedRows = useSelectedRowsStore(
    (state) => state.tables[tableId]?.selectedRows
  );
  const isSelectAll = useSelectedRowsStore(
    (state) => state.tables[tableId]?.isSelectAll
  );
  const initializeTable = useSelectedRowsStore(
    (state) => state.initializeTable
  );

  const { totalProjectsCount } = useProjectsStore((state) => state.allProjects);

  const selectedRowsNumber = useMemo(
    () => (isSelectAll ? totalProjectsCount : selectedRows?.size || 0),
    [isSelectAll, selectedRows, totalProjectsCount]
  );

  const [isExporting, setIsExporting] = useState(false);

  const getProjectsToExport = useCallback(async () => {
    return isSelectAll
      ? getAllProjectsToExportReq(search, filter, pto, undefined, batteryOperationalized)
      : getProjectsToExportReq([...selectedRows]);
  }, [filter, isSelectAll, pto, search, selectedRows, batteryOperationalized]);

  const formatExternalIncentiveProgram = (value?: string) => {
    if (!value) return "";
    const v = String(value).trim().toLowerCase();
    const map: Record<string, string> = {
      "dac-sash": "DAC-SASH",
      sgip: "SGIP",
      "city-solar-equity": "City of San Diego's Solar Equity Program",
      other: "Other",
      none: "None",
    };
    return map[v] ?? value;
  };

  const formatPayeeType = (value?: string) => {
    if (!value) return "";
    const v = String(value).trim().toLowerCase();
    const map: Record<string, string> = {
      contractor: "Contractor",
      customer: "Customer",
      admin: "Admin",
      administrator: "Admin",
    };
    return map[v] ?? capitalizeFirstLetter(v);
  };

  // Helper function to get first non-empty value from multiple field options
  const getFieldValue = (obj: any, ...fieldNames: string[]): string => {
    for (const fieldName of fieldNames) {
      const value = obj?.[fieldName];
      if (value !== undefined && value !== null && value !== "") {
        return String(value);
      }
    }
    return "";
  };

  const handleExportProjects = async () => {
    try {
      if (!isExporting) {
        setIsExporting(true);
        const response = await getProjectsToExport();
        const transformedData = response?.data?.projects?.map(
          (el: ProjectApiResponse) => {
            const projectExtensions = el?.ProjectExtensions ?? [];
            const projectExtension =
              projectExtensions?.[projectExtensions.length - 1];

            const getProjectType = () => {
              if (el?.sdcpProject?.CARE === "Y") return "CARE";
              if (el?.sdcpProject?.FERA === "Y") return "FERA";
              if (el?.sdcpProject?.isCoC === "Y") return "CoC";
              return "Market Rate";
            };

            const mailingState =
              getFieldValue(
                el,
                "mailingState",
                "Mailing_State",
                "applicantState",
                "state"
              ) ||
              el?.sdcpProject?.State ||
              "";

            const customerFullName =
              el?.Customer_First_Name && el?.Customer_Last_Name
                ? `${el?.Customer_First_Name} ${el?.Customer_Last_Name}`.trim()
                : "";

            const payeeName =
              el?.payeeName ||
              el?.payee ||
              customerFullName ||
              el?.customerName ||
              "";

            const mailingAddress = getFieldValue(
              el,
              "mailingAddress",
              "Mailing_Address",
              "address"
            );

            const mailingCity = getFieldValue(
              el,
              "mailingCity",
              "Mailing_City",
              "city"
            );

            const mailingZipCode = getFieldValue(
              el,
              "mailingZipCode",
              "Mailing_Zip_Code",
              "zipCode"
            );

            const applicantState =
              getFieldValue(
                el,
                "applicantState",
                "state",
                "mailingState",
                "Mailing_State"
              ) ||
              el?.sdcpProject?.State ||
              "";

            const totalCapacityIncentive = isContractor
              ? ""
              : calculateTotalUsableCapacity(
                  el?.usableCapacityIncentive,
                  el?.numberOfBatteries
                );

            const totalUsableCapacity =
              el?.totalCapacity ||
              calculateTotalUsableCapacity(
                el?.usableCapacity,
                el?.numberOfBatteries
              );
            const formattedTotalUsableCapacity = totalUsableCapacity
              ? removeTrailZero(totalUsableCapacity)
              : "";

            const nominalPower = el?.nominalPowerkw || el?.ptoNameplateKw || "";
            const formattedNominalPower = nominalPower
              ? removeTrailZero(nominalPower)
              : "";
            const totalNominalPower = calculateTotalNominalPower(
              nominalPower,
              el?.numberOfBatteries
            );

            return {
              id: el?.id || "",
              userId: el?.userId || "",
              customerName: el?.customerName?.trim() || "",
              address: el?.address?.trim() || "",
              city: el?.city || "",
              zipCode: el?.zipCode || "",
              state: applicantState,
              accountHash: el?.accountHash || "",
              customerEmail: el?.accountEmail || "",
              phoneNumber: (el as any)?.phoneNumber || "",
              payeeType: formatPayeeType(el?.payeeType),
              payeeName: payeeName || "",
              mailingAddress: mailingAddress?.trim() || "",
              mailingCity: mailingCity || "",
              mailingState: mailingState || "",
              mailingZipCode: mailingZipCode || "",
              existingInverter: el?.existingInverter || "",
              inverterManufacturer: el?.inverterManufacturer || "",
              inverterName: el?.inverterName || el?.existingInverter || "",
              solarKwdc: removeTrailZero(el?.solarKwdc || ""),
              solarKwac: removeTrailZero(el?.solarKwac || ""),
              solarAnnualProduction: removeTrailZero(
                el?.solarAnnualProduction || ""
              ),
              newSolarKwdc: removeTrailZero(el?.newSolarKwdc || ""),
              newSolarKwac: removeTrailZero(el?.newSolarKwac || ""),
              newSolarAnnualProduction: removeTrailZero(
                el?.newSolarAnnualProduction || ""
              ),
              incentiveStatus: capitalizeFirstLetter(
                replaceUnderscoresWithSpaces(el?.incentiveStatus ?? "")
              ),
              enrollmentStatus: capitalizeFirstLetter(
                replaceUnderscoresWithSpaces(el?.enrolmentStatus ?? "")
              ),
              incentiveProgramLeveraged: formatExternalIncentiveProgram(
                el?.externalIncentivePrograms
              ),
              upfrontIncentivePayment: el?.upfrontIncentivePayment
                ? formatCurrency(Number(el.upfrontIncentivePayment) / 100)
                : "",
              contractor: el?.contractor || "",
              contractorId: el?.contractorId || "",
              batteryOem: el?.batteryOem || "",
              batteryModel: el?.batteryModel || "",
              batterySerialNumber: el?.batterySerialNumber || "",
              usableCapacity: removeTrailZero(el?.usableCapacity || ""),
              usableCapacityIncentive: isContractor
                ? ""
                : removeTrailZero(el?.usableCapacityIncentive || ""),
              totalCapacityIncentive,
              totalUsableCapacity: formattedTotalUsableCapacity,
              nominalPower: formattedNominalPower,
              totalNominalPower,
              numberOfBatteries: el?.numberOfBatteries || "",
              summerEnrollment: `${el?.summerEnrolment || 0}%`,
              winterEnrollment: `${el?.winterEnrolment || 0}%`,
              nemSbp: el?.sdcpProject?.NEM_SBP || "NEM",
              type: getProjectType(),
              careEligible:
                el?.careEligible || el?.sdcpProject?.CARE === "Y"
                  ? "Yes"
                  : "No",
              feraEligible:
                el?.feraEligible || el?.sdcpProject?.FERA === "Y"
                  ? "Yes"
                  : "No",
              cocEligible:
                el?.cocEligible || el?.sdcpProject?.isCoC === "Y"
                  ? "Yes"
                  : "No",
              createdAt: formatDate(el?.createdAt),
              updatedAt: el?.updatedAt
                ? dayjs(el?.updatedAt).format("MMMM D, YYYY h:mm a")
                : "",
              approvalDate: el?.approvalDate
                ? dayjs(el?.approvalDate).format("MMMM D, YYYY h:mm a")
                : "",
              estimatedInstallationDate: el?.estimated_installation_date
                ? dayjs(el?.estimated_installation_date).format("MMMM D, YYYY")
                : "",
              // camelCase — returned by Sequelize model, not snake_case like some raw query fields
              // SDCP-1296: header is "Operationalized Date"; format in Pacific
              // time so the date matches the portal (no off-by-one for exporters
              // in timezones ahead of UTC).
              operationalizedDate: el?.operationalizedDate
                ? formatExportDate(el.operationalizedDate)
                : "",
              sizingSubmitted: el?.sizing_submitted ? "Yes" : "No",
              sizingPassed: (() => {
                if (el?.sizing_passed === 1) return "Yes";
                if (el?.sizing_passed === 0) return "No";
                return "";
              })(),
              ptoNameplateKw: removeTrailZero(el?.ptoNameplateKw || ""),
              totalCost: el?.total_cost
                ? formatCurrency(Number(el.total_cost) / 100)
                : "",
              externalIncentives: (() => {
                const value =
                  el?.externalIncentives || (el as any)?.extra_incentives;
                if (value === null || value === undefined || value === "")
                  return "";
                // Handle string "0.00" or number 0 - still show it
                const numValue =
                  typeof value === "string"
                    ? Number.parseFloat(value)
                    : Number(value);
                if (Number.isNaN(numValue)) return "";
                // If value is already in dollars (has decimal), use as-is, otherwise divide by 100
                const dollarValue =
                  numValue < 1000 && numValue % 1 !== 0
                    ? numValue
                    : numValue / 100;
                return formatCurrency(dollarValue);
              })(),
              thirdPartyOwned: el?.thirdPartyOwned ? "Yes" : "No",
              complete: el?.complete ? "Yes" : "No",
            };
          }
        );

        downloadCSV(transformedData, "projects", ProjectsCSVHeaders);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsExporting(false);
    }
  };

  // Initialize the table
  useEffect(() => {
    initializeTable(tableId);
  }, [initializeTable, tableId]);

  if (!selectedRowsNumber) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        alignItems: "center",
        justifyContent: "flex-end",
        ...(downSm && { paddingLeft: "24px" }),
      }}
    >
      <Button
        onClick={handleExportProjects}
        startIcon={<SVGDownloadFile />}
        variant={"outlined"}
      >
        {isExporting ? "Exporting..." : "Export"} {selectedRowsNumber}{" "}
        {selectedRowsNumber > 1 ? "Projects" : "Project"}
      </Button>
    </Box>
  );
};

export default ProjectsSelectedInfo;
